import { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import {
  ArrowLeft, Sparkle, Gear, UserCircle, Kanban, Presentation, Warning, Lightbulb, CalendarBlank,
  Printer, ArrowsClockwise, Translate, ClockCounterClockwise, CaretDown, ArrowUUpLeft, Archive,
} from '@phosphor-icons/react';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { toast } from 'sonner';
import { formatApiErrorDetail, useAuth } from '@/auth/AuthContext';
import { openPrintWindow } from '@/lib/printWindow';
import { AiProgressBanner } from '@/components/AiProgressBanner';
import { engApi } from './engApi';
import { buildDigestPrintHtml } from './engDigestPrint';
import { formatShort } from '@/utils/datetime';
import { VL, canCap, versionErrorText } from './digest/digestVersionI18n';
import { DigestVersionsPanel } from './digest/DigestVersionsPanel';
import { DigestCompareDialog } from './digest/DigestCompareDialog';
import { DigestActivateDialog, DigestRestoreDialog } from './digest/DigestActivateDialog';
import { DigestCandidateBanner, DigestVersionBanner, DigestLegacyBanner } from './digest/DigestCandidateBanner';
import { LifecycleBadge } from './digest/DigestVersionBadge';
import { jstDateTimeShort } from '@/utils/jstDate';

const DOW = ['月', '火', '水', '木', '金', '土', '日'];
const fmtDT = (s) => (s ? formatShort(s) : '—');

// AIの応答が配列でなく文字列で返ってくるケース（翻訳のセクション分割時に稀に発生）
// にも耐えるよう、rendering 前に必ず配列へ正規化する。
const asArr = (v) => {
  if (Array.isArray(v)) return v;
  if (v === null || v === undefined || v === '') return [];
  if (typeof v === 'string') {
    // 改行 / スラッシュ / 中黒 / 句点でざっくり分割
    return v.split(/\n|(?: \/ )|、|・/).map((s) => s.trim()).filter(Boolean);
  }
  return [v];
};

const UI_LBL = {
  ja: {
    mgmtBanner: 'Management Digest', mgmtTitle: '経営向けサマリー', execSummary: 'エグゼクティブサマリー',
    accomplishments: '主な成果', activeProjects: '進行中のプロジェクト', risks: 'リスクの兆候',
    blocked: 'ブロック中の活動', lessons: '教訓・学び', upcoming: '来週の予定', themes: '戦略テーマ',
    engineers: 'エンジニア別サマリー', projects: 'プロジェクト別サマリー', projLabel: 'プロジェクト',
    done: '成果', blockers: 'ブロッカー', nextActions: '次のアクション',
    completed: '完了', ongoing: '進行中', nextWeek: '来週',
    aiFeedback: 'AIアシスタント フィードバック', aiFbHint: 'AIが答えられなかった質問 — ナレッジの追加・修正のヒントに',
    aiFbNone: '今週の低評価はありません', aiFbQ: '質問', aiFbA: 'AIの回答（抜粋）',
    stats: (d) => `日報 ${d.report_count}件 ・ エンジニア ${d.engineer_count}名 ・ プロジェクト ${d.project_count}件`,
  },
  en: {
    mgmtBanner: 'Management Digest', mgmtTitle: 'Management Summary', execSummary: 'Executive Summary',
    accomplishments: 'Key Accomplishments', activeProjects: 'Active Projects', risks: 'Risk Signals',
    blocked: 'Blocked Activities', lessons: 'Lessons Learned', upcoming: 'Upcoming (Next Week)', themes: 'Strategic Themes',
    engineers: 'Per-Engineer Summary', projects: 'Per-Project Summary', projLabel: 'Projects',
    done: 'Done', blockers: 'Blockers', nextActions: 'Next actions',
    completed: 'Completed', ongoing: 'Ongoing', nextWeek: 'Next week',
    aiFeedback: 'AI Assistant Feedback', aiFbHint: 'Questions the AI could not answer well — hints for knowledge fixes',
    aiFbNone: 'No low-rated answers this week', aiFbQ: 'Question', aiFbA: 'AI answer (excerpt)',
    stats: (d) => `${d.report_count} reports · ${d.engineer_count} engineers · ${d.project_count} projects`,
  },
};

const List = ({ items }) => {
  const arr = asArr(items);
  return arr.length === 0 ? <p className="text-[12px] text-zinc-400">—</p> : (
    <ul className="list-disc pl-4 space-y-0.5">
      {arr.map((x, i) => <li key={i} className="text-[13px] text-zinc-700 leading-relaxed">{x}</li>)}
    </ul>
  );
};

function MgmtSection({ title, Icon, tone, children }) {
  return (
    <div>
      <h4 className={`text-[12px] font-bold ${tone} flex items-center gap-1.5 uppercase tracking-wider`}>
        <Icon size={13} weight="duotone" /> {title}
      </h4>
      <div className="mt-1">{children}</div>
    </div>
  );
}

const DIGEST_STEPS = ['エンジニア別サマリー', 'プロジェクト別サマリー', '経営向けサマリー', '整理・保存'];
// Spec §PROGRESS_DISPLAY — 4 stages for English-only translation:
// 1. read saved Japanese → 2. translate → 3. validate → 4. save (完了 = banner hidden)
const TRANSLATE_STEPS = ['日本語を読み込み', '英訳', '検証', '保存'];

function DigestProgress({ detail, onCancel }) {
  return (
    <AiProgressBanner
      progress={detail.progress}
      steps={DIGEST_STEPS}
      since={detail.started_at}
      tone="violet"
      defaultLabel="AIが週間ダイジェストを生成しています…"
      footer="他のページに移動しても生成はバックグラウンドで続行されます（通常 1〜2 分）。"
      testIdPrefix="digest-progress"
      onCancel={onCancel}
    />
  );
}

function TranslateProgress({ detail, onCancel }) {
  return (
    <AiProgressBanner
      progress={detail.english_progress}
      steps={TRANSLATE_STEPS}
      since={detail.english_started_at}
      tone="indigo"
      defaultLabel="AIが英訳を生成しています…"
      footer="他のページに移動しても翻訳はバックグラウンドで続行されます（通常 1〜2 分）。"
      testIdPrefix="digest-translate-progress"
      onCancel={onCancel}
      cancelLabel="英訳を中止"
    />
  );
}

export default function EngReportDigests() {
  const { user } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const [digests, setDigests] = useState([]);
  const [sel, setSel] = useState(null);
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [settings, setSettings] = useState(null);
  const [lang, setLang] = useState('ja');
  const [translating, setTranslating] = useState(false);
  const [regenerating, setRegenerating] = useState(false);
  const [regenOpen, setRegenOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [enRegenOpen, setEnRegenOpen] = useState(false);       // 「英訳のみ再生成」確認
  const [historyOpen, setHistoryOpen] = useState(false);       // 英訳履歴ダイアログ
  const [historyRows, setHistoryRows] = useState(null);
  const [regenMenuOpen, setRegenMenuOpen] = useState(false);   // 再生成メニュー
  const [trend, setTrend] = useState(null);
  // Stage F — 不変バージョン履歴
  const [viewVersion, setViewVersion] = useState(null);        // 表示中の履歴/候補バージョン（null = 有効版）
  const [versionsOpen, setVersionsOpen] = useState(false);
  const [versionsRefresh, setVersionsRefresh] = useState(0);
  const [comparePair, setComparePair] = useState(null);
  const [activateTarget, setActivateTarget] = useState(null);
  const [restoreTarget, setRestoreTarget] = useState(null);
  const [migrating, setMigrating] = useState(false);
  const isAdmin = user?.role === 'admin';
  const can = {
    view: canCap(user, 'digest_versions_view'), compare: canCap(user, 'digest_versions_compare'),
    activate: canCap(user, 'digest_activate'), activateWarn: canCap(user, 'digest_activate_with_warnings'),
    restore: canCap(user, 'digest_restore'), aiLogs: canCap(user, 'ai_logs_view'),
  };

  const load = () => {
    setLoading(true);
    engApi.reportDigests().then((list) => {
      setDigests(list);
      const deep = searchParams.get('d');
      if (deep && list.some((x) => x.id === deep)) setSel(deep);
      else if (list.length > 0 && !sel) setSel(list[0].id);
    }).catch((e) => toast.error(formatApiErrorDetail(e.response?.data?.detail) || '読み込みに失敗しました'))
      .finally(() => setLoading(false));
    engApi.reportDigestsTrend(8).then(setTrend).catch(() => setTrend(null));
  };
  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const refreshDetail = async (id = sel) => {
    if (!id) return null;
    const d = await engApi.reportDigest(id).catch(() => null);
    if (d) {
      setDetail((cur) => (cur && cur.id === d.id ? d : cur));
      setDigests((list) => list.map((row) => (row.id === d.id
        ? { ...row, status: d.status, error: d.error, report_count: d.report_count, engineer_count: d.engineer_count,
            project_count: d.project_count, generated_at: d.generated_at, generated_by: d.generated_by,
            active_version_id: d.active_version_id, active_version_number: d.active_version_number,
            version_count: d.version_count, candidate: d.versions_summary?.latest_candidate || null }
        : row)));
    }
    return d;
  };

  const openVersion = async (sid, vid) => {
    try {
      const v = await engApi.digestVersion(sid, vid);
      setViewVersion(v);
      setLang('ja');
    } catch (e) {
      toast.error(versionErrorText(e, lang, '取得に失敗しました / Failed to load'));
    }
  };

  useEffect(() => {
    if (!sel) { setDetail(null); setViewVersion(null); return; }
    setViewVersion(null);
    engApi.reportDigest(sel).then((d) => {
      setDetail(d);
      const cand = d?.versions_summary?.latest_candidate;
      const deepV = searchParams.get('v');
      if (deepV && d?.versions_summary?.versioned) {
        openVersion(d.id, deepV);
        setSearchParams({}, { replace: true });
      } else if (d?.versions_summary?.versioned && !d.active_version_id && cand
                 && ['complete', 'complete_with_warnings'].includes(cand.status)) {
        // 有効版がない → 最新候補を（読み取り専用の候補として）自動表示
        openVersion(d.id, cand.id);
      }
      // 別セッションで開始された生成中ジョブがある場合はそのまま polling を続ける
      if (cand && ['running', 'queued'].includes(cand.status)) {
        pollCandidate(d.id, cand.id);
      } else if (d && d.status === 'running' && !d.versions_summary?.versioned) {
        pollUntilDone(d.id);
      }
      // 別セッションで開始された翻訳ジョブがある場合も polling で状態を追う
      if (d && d.english_status === 'running' && !d.english_version && !translating) {
        pollTranslationUntilDone(d.id);
      }
    }).catch(() => setDetail(null));
  }, [sel]); // eslint-disable-line react-hooks/exhaustive-deps

  // 候補バージョンの完了/失敗まで polling — 有効版はそのまま閲覧可能
  const pollCandidate = async (sid, vid) => {
    const started = Date.now();
    while (Date.now() - started < 8 * 60 * 1000) {
      await new Promise((r) => setTimeout(r, 3000));
      try {
        const v = await engApi.digestVersion(sid, vid);
        setDetail((cur) => (cur && cur.id === sid
          ? { ...cur, versions_summary: { ...(cur.versions_summary || {}), latest_candidate: v } } : cur));
        if (!['running', 'queued'].includes(v.status)) {
          const d = await refreshDetail(sid);
          setVersionsRefresh((n) => n + 1);
          return { version: v, series: d };
        }
      } catch (e) {
        if (e?.response?.status === 404) return null;
      }
    }
    return null;
  };

  const afterGeneration = (res, verb) => {
    const v = res?.version;
    const s = res?.series;
    if (!v) { toast.warning(`${verb}が完了しませんでした。しばらくしてから確認してください`); return; }
    if (v.status === 'failed') { toast.error(v.error || `${verb}に失敗しました`); return; }
    if (v.status === 'cancelled') { toast.info(`${verb}を中止しました`); return; }
    if (s && s.active_version_id === v.id) {
      toast.success(`ダイジェストを${verb}し、有効版（v${v.version_number}）になりました`);
    } else if (v.status === 'complete_with_warnings') {
      toast.warning(`候補 v${v.version_number} は一部のみ生成されました。有効版は変更されていません — 内容を確認して有効化してください`);
    } else {
      toast.success(`候補 v${v.version_number} を${verb}しました。内容を比較・確認して有効化してください`);
    }
    if (s && !s.active_version_id && ['complete', 'complete_with_warnings'].includes(v.status)) openVersion(s.id, v.id);
  };

  // 英語版翻訳の完了/失敗まで自動ポーリング
  const pollTranslationUntilDone = async (digestId) => {
    const started = Date.now();
    while (Date.now() - started < 8 * 60 * 1000) {
      await new Promise((r) => setTimeout(r, 3000));
      try {
        const d = await engApi.reportDigest(digestId);
        setDetail((cur) => (cur && cur.id === digestId ? { ...cur, ...d } : cur));
        if (d.english_version) return d;
        if (d.english_status === 'failed') return d;
      } catch { /* 一時的な取得失敗は無視 */ }
    }
    return null;
  };

  // ダイジェストが running 状態のとき、完了/失敗まで自動ポーリングする。
  // Cloudflare の 100 秒タイムアウトを回避するため、実処理はサーバー側の
  // asyncio.create_task にオフロードし、フロントは 3 秒間隔で status を確認する。
  const pollUntilDone = async (digestId) => {
    const started = Date.now();
    const timeoutMs = 8 * 60 * 1000; // 8分で諦める（AIも普通は1〜2分）
    while (Date.now() - started < timeoutMs) {
      await new Promise((r) => setTimeout(r, 3000));
      try {
        const d = await engApi.reportDigest(digestId);
        setDetail((cur) => (cur && cur.id === digestId ? d : cur));
        setDigests((list) => list.map((row) => (row.id === digestId
          ? { ...row, status: d.status, error: d.error, report_count: d.report_count,
              engineer_count: d.engineer_count, project_count: d.project_count,
              generated_at: d.generated_at, generated_by: d.generated_by }
          : row)));
        if (d.status && d.status !== 'running') return d;
      } catch (e) {
        if (e?.response?.status === 404) return null;
      }
    }
    return null;
  };

  const generate = async () => {
    setGenerating(true);
    try {
      const d = await engApi.generateReportDigest({}, { uiLang: lang, selectedId: sel });
      // プレースホルダを list に即差し込み（生成中バッジを見せる）— 既存シリーズなら候補として追加される
      setDigests((list) => {
        const exists = list.some((x) => x.id === d.id);
        return exists ? list.map((x) => (x.id === d.id ? { ...x, candidate: d.candidate } : x)) : [d, ...list];
      });
      if (sel === d.id) {
        setDetail((cur) => (cur ? { ...cur, versions_summary: { ...(cur.versions_summary || {}), versioned: true, latest_candidate: d.candidate } } : cur));
      } else {
        setSel(d.id);
      }
      toast.info(d.active_version_id
        ? `新しい候補 v${d.version_number} を生成中です。現在の有効版はそのまま閲覧できます（1〜2分）…`
        : 'ダイジェストを生成中です（1〜2分ほどかかります）…');
      afterGeneration(await pollCandidate(d.id, d.version_id), '生成');
    } catch (e) {
      toast.error(versionErrorText(e, lang, formatApiErrorDetail(e.response?.data?.detail) || '生成に失敗しました'));
    } finally {
      setGenerating(false);
    }
  };

  const openSettings = async () => {
    try {
      setSettings(await engApi.reportSettings());
      setSettingsOpen(true);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || '設定の読み込みに失敗しました');
    }
  };

  const saveSettings = async () => {
    try {
      await engApi.saveReportSettings(settings);
      toast.success('設定を保存しました');
      setSettingsOpen(false);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || '保存に失敗しました');
    }
  };

  // 表示対象: 履歴/候補バージョンを開いている間はその内容（日本語のみ・読み取り専用）、それ以外は有効版
  const shown = viewVersion || detail;
  const mgmtJa = shown?.management_digest || {};
  const useEn = !viewVersion && lang === 'en' && !!detail?.english_version;
  const L = UI_LBL[useEn ? 'en' : 'ja'];
  const VLT = VL[lang] || VL.ja;
  const mgmt = (useEn ? detail.english_version.management_digest : mgmtJa) || {};
  const engSums = asArr(useEn ? detail.english_version.engineer_summaries : shown?.engineer_summaries);
  const projSums = asArr(useEn ? detail.english_version.project_summaries : shown?.project_summaries);
  const versioned = !!detail?.versions_summary?.versioned;
  const legacy = !!detail && detail.versions_summary?.versioned === false;
  const candRunning = ['running', 'queued'].includes(detail?.versions_summary?.latest_candidate?.status);
  const busyGenerating = regenerating || candRunning || (detail?.status === 'running' && !versioned);

  const afterVersionChange = async () => {
    setViewVersion(null);
    await refreshDetail(sel);
    setVersionsRefresh((n) => n + 1);
    engApi.reportDigests().then(setDigests).catch(() => {});
  };

  const runMigration = async (dryRun) => {
    setMigrating(true);
    try {
      const r = await engApi.migrateDigestVersions(dryRun);
      if (dryRun) toast.info(VLT.dryResult(r));
      else { toast.success(VLT.migrated(r)); await afterVersionChange(); }
    } catch (e) {
      toast.error(versionErrorText(e, lang, '移行に失敗しました / Migration failed'));
    } finally { setMigrating(false); }
  };

  const translate = async (id = sel, opts = {}) => {
    setTranslating(true);
    const syncRow = (patch) => setDigests((list) => list.map(
      (row) => (row.id === id ? { ...row, ...patch } : row)));
    try {
      const res = await engApi.translateReportDigest(id, opts);
      if (res.status === 'complete' && res.english_version) {
        setDetail((d) => (d && d.id === id ? { ...d, english_version: res.english_version, english_outdated: false } : d));
        syncRow({ english_status: 'complete', english_version: res.english_version, english_outdated: false });
        toast.success(opts.force ? '英訳を再生成しました' : '英語版を生成しました');
        return;
      }
      // バックグラウンド翻訳 — english_version の出現まで polling
      // 進捗表示のため、poll ごとに english_progress/english_started_at を反映
      setDetail((d) => (d && d.id === id ? {
        ...d,
        english_status: 'running',
        english_progress: res.english_progress || { step: 1, total: 4, label: '翻訳を開始しています…' },
        english_started_at: new Date().toISOString(),
      } : d));
      syncRow({ english_status: 'running', english_error: null });
      toast.info('英語版を生成中です（1〜2分ほどかかります）…');
      const started = Date.now();
      while (Date.now() - started < 8 * 60 * 1000) {
        await new Promise((r) => setTimeout(r, 3000));
        try {
          const d = await engApi.reportDigest(id);
          setDetail((cur) => (cur && cur.id === id ? { ...cur, ...d } : cur));
          if (d.english_version) {
            syncRow({ english_status: 'complete', english_version: d.english_version });
            toast.success('英語版を生成しました');
            return;
          }
          if (d.english_status === 'failed') {
            syncRow({ english_status: 'failed', english_error: d.english_error });
            toast.error(d.english_error || '英語版の生成に失敗しました');
            return;
          }
        } catch { /* 一時的な取得失敗は無視して継続 */ }
      }
      toast.warning('英語版の生成が完了しませんでした。しばらくしてから再度お試しください');
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || '英語版の生成に失敗しました');
    } finally {
      setTranslating(false);
    }
  };

  const cancelRunning = async () => {
    if (!detail?.id) return;
    if (!window.confirm('この生成を中止しますか?')) return;
    try {
      await engApi.cancelReportDigest(detail.id);
      toast.success('中止リクエストを送信しました');
      // Refresh detail — polling loop will detect status change too
      const fresh = await engApi.reportDigest(detail.id).catch(() => null);
      if (fresh) setDetail((cur) => (cur && cur.id === fresh.id ? { ...cur, ...fresh } : cur));
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || '中止に失敗しました');
    }
  };

  const cancelTranslation = async () => {
    if (!detail?.id) return;
    if (!window.confirm('英訳を中止しますか? 保存済みの日本語ダイジェストは変更されません。')) return;
    try {
      await engApi.cancelReportDigestTranslation(detail.id);
      toast.success('英訳の中止リクエストを送信しました');
      const fresh = await engApi.reportDigest(detail.id).catch(() => null);
      if (fresh) setDetail((cur) => (cur && cur.id === fresh.id ? { ...cur, ...fresh } : cur));
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || '中止に失敗しました');
    }
  };

  const loadTranslationHistory = async () => {
    if (!sel) return;
    setHistoryOpen(true); setHistoryRows(null);
    try {
      const r = await engApi.reportDigestTranslationHistory(sel);
      setHistoryRows(r.rows || []);
    } catch (e) {
      setHistoryRows([]);
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || '英訳履歴の取得に失敗しました');
    }
  };

  const restoreTranslation = async (tid) => {
    if (!sel) return;
    try {
      await engApi.restoreReportDigestTranslation(sel, tid);
      const fresh = await engApi.reportDigest(sel);
      setDetail((cur) => (cur && cur.id === fresh.id ? { ...cur, ...fresh } : cur));
      await loadTranslationHistory();
      toast.success('英訳を復元しました');
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || '復元に失敗しました');
    }
  };

  const regenerate = async () => {
    setRegenOpen(false);
    setRegenerating(true);
    try {
      const d = await engApi.regenerateReportDigest(sel, { uiLang: lang });
      // 有効版・英訳・履歴はそのまま。候補の進捗だけを detail に差し込む。
      setDetail((cur) => (cur && cur.id === d.id
        ? { ...cur, versions_summary: { ...(cur.versions_summary || {}), versioned: true, latest_candidate: d.candidate } } : cur));
      setDigests((list) => list.map((row) => (row.id === d.id ? { ...row, candidate: d.candidate } : row)));
      setViewVersion(null);
      toast.info(`新しい候補 v${d.version_number} を生成中です。現在の有効版は置き換えられません（1〜2分）…`);
      afterGeneration(await pollCandidate(d.id, d.version_id), '再生成');
    } catch (e) {
      toast.error(versionErrorText(e, lang, formatApiErrorDetail(e.response?.data?.detail) || '再生成に失敗しました'));
    } finally {
      setRegenerating(false);
    }
  };

  // 管理者のみ: アーカイブ（論理削除）— バージョン履歴・AI実行証跡は削除されない
  const remove = async () => {
    setDeleteOpen(false);
    setDeleting(true);
    try {
      await engApi.deleteReportDigest(sel);
      toast.success(VLT.archived);
      const removedId = sel;
      setDetail(null);
      setViewVersion(null);
      setSel(null);
      const list = await engApi.reportDigests();
      setDigests(list);
      const next = list.find((d) => d.id !== removedId);
      if (next) setSel(next.id);
    } catch (e) {
      toast.error(versionErrorText(e, lang, formatApiErrorDetail(e.response?.data?.detail) || 'アーカイブに失敗しました'));
    } finally {
      setDeleting(false);
    }
  };

  const doPrint = () => {
    if (!shown) return;
    const vMeta = viewVersion || (versioned && detail.active_version_id
      ? { version_number: detail.active_version_number, lifecycle: 'active' } : null);
    openPrintWindow(buildDigestPrintHtml(shown, useEn ? 'en' : 'ja', vMeta));
  };

  return (
    <div className="space-y-4" data-testid="eng-report-digests-page">
      <div className="flex flex-wrap items-center gap-2">
        <Button size="sm" variant="ghost" asChild className="h-8 -ml-2 text-zinc-500">
          <Link to="/engineering/reports"><ArrowLeft size={14} className="mr-1" /> 日報一覧</Link>
        </Button>
        <h1 className="text-lg sm:text-xl font-bold text-zinc-900 flex items-center gap-2">
          <Sparkle size={22} weight="duotone" className="text-violet-600" /> 週間AIダイジェスト
        </h1>
        <div className="ml-auto flex items-center gap-2">
          <Button size="sm" variant="outline" onClick={openSettings} className="h-8" data-testid="digest-settings-btn">
            <Gear size={14} className="mr-1" /> 設定
          </Button>
          <Button size="sm" onClick={generate} disabled={generating} className="h-8 bg-teal-700 hover:bg-teal-800" data-testid="digest-generate-btn">
            <Sparkle size={14} className="mr-1" /> {generating ? '生成中…（1〜2分お待ちください）' : '今週分を今すぐ生成'}
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-[240px_1fr] gap-4 items-start">
        <div className="space-y-4">
          {trend && (trend.points || []).length >= 2 && (
            <div className="bg-white rounded-xl border border-zinc-200 shadow-sm p-3" data-testid="digest-trend-card">
              <div className="flex items-center gap-1 mb-2">
                <Presentation size={13} className="text-violet-500" />
                <p className="text-[11px] font-bold text-zinc-500 uppercase tracking-wider">直近{trend.points.length}週トレンド</p>
              </div>
              {(() => {
                const metrics = [
                  { key: 'report_count', label: '日報件数', color: 'bg-emerald-500', accent: 'text-emerald-700' },
                  { key: 'blockers', label: 'ブロッカー', color: 'bg-rose-500', accent: 'text-rose-700' },
                  { key: 'risks', label: 'リスク', color: 'bg-amber-500', accent: 'text-amber-700' },
                  { key: 'wins', label: '成果', color: 'bg-sky-500', accent: 'text-sky-700' },
                ];
                return (
                  <div className="space-y-2.5">
                    {metrics.map((m) => {
                      const vals = trend.points.map((p) => Number(p[m.key] || 0));
                      const max = Math.max(1, ...vals);
                      const dv = trend.deltas?.[m.key];
                      const arrow = dv == null ? '' : dv > 0 ? '▲' : dv < 0 ? '▼' : '＝';
                      const arrowColor = dv == null ? 'text-zinc-400'
                        : (m.key === 'blockers' || m.key === 'risks')
                          ? (dv > 0 ? 'text-rose-600' : 'text-emerald-600')
                          : (dv > 0 ? 'text-emerald-600' : 'text-rose-600');
                      return (
                        <div key={m.key} data-testid={`digest-trend-${m.key}`}>
                          <div className="flex items-center justify-between text-[11px] mb-0.5">
                            <span className={`font-semibold ${m.accent}`}>{m.label}</span>
                            <span className={`font-mono tabular-nums ${arrowColor}`} title="前週差">
                              {arrow} {dv == null ? '—' : `${dv > 0 ? '+' : ''}${dv}`}
                            </span>
                          </div>
                          <div className="flex items-end gap-0.5 h-9">
                            {vals.map((v, i) => (
                              <div key={i} className="flex-1 flex flex-col items-center justify-end"
                                title={`${trend.points[i].week_start} 〜 ${trend.points[i].week_end}: ${v}`}>
                                <span className={`w-full ${m.color} rounded-t opacity-80 hover:opacity-100 transition-opacity`}
                                      style={{ height: `${Math.max(3, (v / max) * 32)}px` }} />
                              </div>
                            ))}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                );
              })()}
              <p className="mt-2 pt-2 border-t border-zinc-200 text-[10px] text-zinc-400">前週差は最新週 vs 前週</p>
            </div>
          )}
          <div className="bg-white rounded-xl border border-zinc-200 shadow-sm overflow-hidden" data-testid="digest-list">
          <p className="px-4 py-2.5 text-[11px] font-bold text-zinc-400 uppercase tracking-wider border-b border-zinc-200">ダイジェスト一覧（週別）</p>
          {loading ? (
            <div className="px-4 py-8 text-center"><div className="w-6 h-6 mx-auto rounded-full border-4 border-zinc-200 border-t-violet-600 animate-spin" /></div>
          ) : digests.length === 0 ? (
            <p className="px-4 py-8 text-[12px] text-zinc-400 text-center">まだ生成されていません</p>
          ) : (
            <div className="divide-y divide-zinc-200 max-h-[60vh] overflow-y-auto">
              {digests.map((d) => (
                <button key={d.id} type="button" onClick={() => setSel(d.id)}
                  className={`w-full text-left px-4 py-2.5 transition-colors ${sel === d.id ? 'bg-violet-50' : 'hover:bg-zinc-50'}`}
                  data-testid={`digest-item-${d.id}`}>
                  <p className="text-[13px] font-semibold text-zinc-800 flex items-center gap-1">
                    <CalendarBlank size={12} className="text-zinc-400" /> {d.week_start} 〜 {d.week_end}
                    {(d.status === 'running' || ['running', 'queued'].includes(d.candidate?.status)) && (
                      <span className="ml-1 inline-flex items-center gap-1 rounded-full bg-violet-100 px-2 py-0.5 text-[10px] font-semibold text-violet-700"
                        data-testid={`digest-item-running-${d.id}`}>
                        <ArrowsClockwise size={10} className="animate-spin" /> {d.candidate?.version_number ? `候補 v${d.candidate.version_number} 生成中` : '生成中'}
                      </span>
                    )}
                    {d.active_version_number && (
                      <span className="ml-1 inline-flex items-center rounded-full bg-emerald-50 border border-emerald-200 px-1.5 py-0.5 text-[10px] font-semibold text-emerald-700"
                        title="有効版 / Active version" data-testid={`digest-item-active-version-${d.id}`}>v{d.active_version_number}</span>
                    )}
                    {d.candidate && ['complete', 'complete_with_warnings'].includes(d.candidate.status) && (
                      <span className="ml-1 inline-flex items-center rounded-full bg-indigo-100 px-2 py-0.5 text-[10px] font-semibold text-indigo-700"
                        title="未有効化の候補あり / Candidate awaiting activation" data-testid={`digest-item-candidate-${d.id}`}>候補 v{d.candidate.version_number}</span>
                    )}
                    {d.versioned === false && (
                      <span className="ml-1 inline-flex items-center rounded-full bg-amber-100 px-2 py-0.5 text-[10px] font-semibold text-amber-800"
                        title="旧形式（未移行） / Legacy (not migrated)" data-testid={`digest-item-legacy-${d.id}`}>旧形式</span>
                    )}
                    {d.status === 'failed' && !d.active_version_id && (
                      <span className="ml-1 inline-flex items-center gap-1 rounded-full bg-rose-100 px-2 py-0.5 text-[10px] font-semibold text-rose-700"
                        data-testid={`digest-item-failed-${d.id}`}>
                        <Warning size={10} weight="fill" /> 失敗
                      </span>
                    )}
                    {d.status !== 'failed' && d.status !== 'running'
                      && d.english_status === 'failed' && !d.english_version && (
                      <span role="button" tabIndex={0}
                        onClick={(e) => {
                          e.stopPropagation();
                          if (translating) return;
                          setSel(d.id);
                          translate(d.id);
                        }}
                        onKeyDown={(e) => { if (e.key === 'Enter') { e.stopPropagation(); setSel(d.id); translate(d.id); } }}
                        title={d.english_error || '英語版の生成に失敗しました。クリックで英訳を再実行します。'}
                        className="ml-1 inline-flex items-center gap-1 rounded-full bg-rose-100 px-2 py-0.5 text-[10px] font-semibold text-rose-700 hover:bg-rose-200 cursor-pointer"
                        data-testid={`digest-item-en-retry-${d.id}`}>
                        <ArrowsClockwise size={10} /> 英訳を再実行
                      </span>
                    )}
                  </p>
                  <p className="text-[11px] text-zinc-400 mt-0.5">日報 {d.report_count}件 ・ {fmtDT(d.generated_at)} ・ {d.generated_by}</p>
                </button>
              ))}
            </div>
          )}
        </div>
        </div>

        {detail ? (
          <div className="space-y-4" data-testid="digest-detail">
            <div className="flex flex-wrap items-center gap-2" data-testid="digest-toolbar">
              <div className="flex rounded-lg border border-zinc-200 overflow-hidden" data-testid="digest-lang-toggle">
                {[['ja', '日本語'], ['en', 'English']].map(([v, label]) => (
                  <button key={v} type="button" onClick={() => setLang(v)}
                    data-testid={`digest-lang-${v}`}
                    className={`px-3 py-1.5 text-[11px] font-semibold transition-colors ${
                      lang === v ? 'bg-violet-600 text-white' : 'bg-white text-zinc-500 hover:bg-zinc-50'}`}>
                    {label}
                  </button>
                ))}
              </div>
              <div className="ml-auto flex items-center gap-2">
                {/* 再生成: 全体 vs 英訳のみ をメニューで明示 (spec §3) */}
                {versioned && can.view && (
                  <Button size="sm" variant="outline" onClick={() => setVersionsOpen(true)}
                    className="h-8 text-[12px]" data-testid="digest-versions-btn" title={VLT.history}>
                    <ClockCounterClockwise size={13} className="mr-1" /> {VLT.history}
                    {detail.version_count ? <span className="ml-1 rounded-full bg-zinc-100 px-1.5 text-[10px] text-zinc-600" data-testid="digest-versions-count">{detail.version_count}</span> : null}
                  </Button>
                )}
                <div className="relative">
                  <Button size="sm" variant="outline"
                    onClick={() => setRegenMenuOpen((v) => !v)}
                    disabled={busyGenerating || legacy}
                    title={legacy ? VLT.legacyHint : undefined}
                    className="h-8 text-[12px]" data-testid="digest-regenerate-btn">
                    <ArrowsClockwise size={13} className={`mr-1 ${busyGenerating ? 'animate-spin' : ''}`} />
                    {busyGenerating ? (lang === 'en' ? 'Generating… (1–2 min)' : '生成中…（1〜2分）') : (lang === 'en' ? 'Regenerate' : '再生成')}
                    <CaretDown size={11} className="ml-1" />
                  </Button>
                  {regenMenuOpen && (
                    <>
                      <div className="fixed inset-0 z-40" onClick={() => setRegenMenuOpen(false)} />
                      <div className="absolute right-0 mt-1 w-72 rounded-lg border border-zinc-200 bg-white shadow-lg z-50 p-1"
                        data-testid="digest-regenerate-menu">
                        <button type="button"
                          onClick={() => { setRegenMenuOpen(false); setRegenOpen(true); }}
                          className="w-full text-left px-3 py-2 rounded hover:bg-violet-50 text-[12px]"
                          data-testid="digest-regenerate-full-menu">
                          <p className="font-semibold text-zinc-800 flex items-center gap-1.5">
                            <ArrowsClockwise size={12} weight="bold" className="text-violet-600" />
                            ダイジェスト全体を再生成（新しい候補）
                          </p>
                          <p className="text-[11px] text-zinc-500 mt-0.5">日報から新しい候補バージョンを作成します。有効版は置き換えられません / Creates a new candidate; the active version is kept</p>
                        </button>
                        <button type="button"
                          onClick={() => { setRegenMenuOpen(false); setEnRegenOpen(true); }}
                          disabled={!detail.management_digest || detail.status === 'running'}
                          className="w-full text-left px-3 py-2 rounded hover:bg-indigo-50 text-[12px] disabled:opacity-50"
                          data-testid="digest-regenerate-en-menu">
                          <p className="font-semibold text-zinc-800 flex items-center gap-1.5">
                            <Translate size={12} weight="bold" className="text-indigo-600" />
                            英訳のみ再生成
                          </p>
                          <p className="text-[11px] text-zinc-500 mt-0.5">保存済み日本語ダイジェストから英訳のみを作り直します</p>
                        </button>
                        <button type="button"
                          onClick={() => { setRegenMenuOpen(false); loadTranslationHistory(); }}
                          className="w-full text-left px-3 py-2 rounded hover:bg-zinc-50 text-[12px]"
                          data-testid="digest-history-menu">
                          <p className="font-semibold text-zinc-800 flex items-center gap-1.5">
                            <ClockCounterClockwise size={12} weight="bold" className="text-zinc-600" />
                            英訳履歴
                          </p>
                          <p className="text-[11px] text-zinc-500 mt-0.5">過去の英訳版を確認・復元</p>
                        </button>
                      </div>
                    </>
                  )}
                </div>
                <Button size="sm" variant="outline" onClick={doPrint}
                  disabled={(!viewVersion && lang === 'en' && !detail.english_version) || (detail.status === 'running' && !versioned) || (!viewVersion && versioned && !detail.active_version_id)}
                  className="h-8 text-[12px]" data-testid="digest-print-btn">
                  <Printer size={13} className="mr-1" /> {lang === 'en' ? 'Print (A4)' : '印刷（A4）'}
                </Button>
                {isAdmin && (
                  <Button size="sm" variant="outline" onClick={() => setDeleteOpen(true)} disabled={deleting || candRunning}
                    className="h-8 text-[12px] border-rose-200 text-rose-600 hover:bg-rose-50 hover:text-rose-700"
                    title={`${VLT.archive}（${VLT.adminOnly}）`}
                    data-testid="digest-delete-btn">
                    <Archive size={13} className="mr-1" /> {deleting ? '…' : VLT.archive}
                  </Button>
                )}
              </div>
            </div>

            {legacy && <DigestLegacyBanner lang={lang} isAdmin={isAdmin} busy={migrating} onDryRun={() => runMigration(true)} onMigrate={() => runMigration(false)} />}
            {versioned && (
              <DigestCandidateBanner detail={detail} lang={lang} can={can}
                onView={(v) => openVersion(detail.id, v.id)}
                onCompare={(a, b) => setComparePair([a, b])}
                onActivate={(v) => setActivateTarget(v)}
                onCancel={cancelRunning} />
            )}
            {viewVersion && (
              <DigestVersionBanner version={viewVersion} lang={lang} can={can} activeVersionId={detail.active_version_id}
                onBack={() => setViewVersion(null)}
                onActivate={(v) => setActivateTarget(v)}
                onRestore={(v) => setRestoreTarget(v)}
                onCompare={(a, b) => setComparePair([a, b])} />
            )}
            {detail.status === 'running' && !versioned && <DigestProgress detail={detail} onCancel={cancelRunning} />}
            {detail.status !== 'running' && detail.english_status === 'running' && !detail.english_version && (
              <TranslateProgress detail={detail} onCancel={cancelTranslation} />
            )}
            {detail.status !== 'running' && detail.english_outdated && detail.english_version && (
              <div className="rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 flex items-start gap-3"
                data-testid="digest-translate-outdated-banner">
                <Warning size={18} weight="fill" className="text-amber-600 shrink-0 mt-0.5" />
                <div className="flex-1">
                  <p className="text-[13px] font-semibold text-amber-900">英訳が古くなっています</p>
                  <p className="text-[11px] text-amber-700 mt-0.5">
                    日本語ダイジェストが更新されたため、現在の英訳は古いバージョンです。英訳のみ再生成してください。
                  </p>
                </div>
                <Button size="sm" onClick={() => translate(sel, { force: true })} disabled={translating}
                  className="h-8 text-[12px] bg-amber-600 hover:bg-amber-700"
                  data-testid="digest-translate-outdated-retry-btn">
                  <Translate size={13} className="mr-1" /> 英訳のみ再生成
                </Button>
              </div>
            )}
            {detail.status !== 'running' && detail.english_status === 'failed' && !detail.english_version && (
              <div className="rounded-xl border border-rose-200 bg-rose-50 px-4 py-3 flex items-start gap-3"
                data-testid="digest-translate-failed-banner">
                <Warning size={18} weight="fill" className="text-rose-600 shrink-0 mt-0.5" />
                <div className="flex-1">
                  <p className="text-[13px] font-semibold text-rose-900">英語版の生成に失敗しました</p>
                  <p className="text-[11px] text-rose-700 mt-0.5 whitespace-pre-wrap">
                    {detail.english_error || 'AIサービスが応答しませんでした。もう一度お試しください。'}
                  </p>
                </div>
                <Button size="sm" onClick={() => translate()} disabled={translating}
                  className="h-8 text-[12px] bg-rose-600 hover:bg-rose-700"
                  data-testid="digest-translate-retry-btn">
                  <ArrowsClockwise size={13} className="mr-1" /> 英訳を再実行
                </Button>
              </div>
            )}
            {shown.status === 'complete_with_warnings' && shown.user_warning && (
              <div className="rounded-xl border border-amber-300 bg-amber-50 px-4 py-4 flex items-start gap-3"
                data-testid="digest-partial-banner">
                <Warning size={18} weight="fill" className="text-amber-600 shrink-0 mt-0.5" />
                <div className="flex-1">
                  <p className="text-[13px] font-semibold text-amber-900">
                    {(shown.incomplete_sections || []).some((k) => !((shown.partial_sections || {})[k]))
                      ? '一部のセクションを生成できませんでした' : '一部の内容を生成できませんでした'}
                  </p>
                  <p className="text-[11px] text-amber-800 mt-0.5 whitespace-pre-wrap" data-testid="digest-partial-warning">
                    {shown.user_warning}
                  </p>
                </div>
              </div>
            )}
            {detail.status === 'failed' && !viewVersion && (
              <div className="rounded-xl border border-rose-200 bg-rose-50 px-4 py-4 flex items-start gap-3"
                data-testid="digest-failed-banner">
                <Warning size={18} weight="fill" className="text-rose-600 shrink-0 mt-0.5" />
                <div className="flex-1">
                  <p className="text-[13px] font-semibold text-rose-900">生成に失敗しました</p>
                  <p className="text-[11px] text-rose-700 mt-0.5 whitespace-pre-wrap">
                    {detail.error || 'AIサービスが応答しませんでした。もう一度お試しください。'}
                  </p>
                </div>
                <Button size="sm" onClick={() => setRegenOpen(true)}
                  className="h-8 text-[12px] bg-rose-600 hover:bg-rose-700"
                  data-testid="digest-failed-retry-btn">
                  <ArrowsClockwise size={13} className="mr-1" /> 再試行
                </Button>
              </div>
            )}

            {!viewVersion && lang === 'en' && !detail.english_version && detail.english_status !== 'running' && (
              <div className="rounded-xl border border-indigo-200 bg-indigo-50 px-4 py-3 flex flex-wrap items-center gap-3"
                data-testid="digest-en-banner">
                <Translate size={18} weight="duotone" className="text-indigo-600 shrink-0" />
                <p className="text-[12px] text-indigo-800 flex-1 min-w-[200px]">
                  英語版はまだ生成されていません。AI翻訳で英語版を作成すると、表示と印刷を英語に切り替えられます。
                </p>
                <Button size="sm" onClick={() => translate()} disabled={translating || detail.status === 'running'}
                  className="h-8 bg-teal-700 hover:bg-teal-800 text-[12px]" data-testid="digest-translate-btn">
                  <Translate size={13} className="mr-1" /> {translating ? '翻訳を開始中…' : '英語版を生成'}
                </Button>
              </div>
            )}

            <div className={`${viewVersion ? 'bg-indigo-800' : 'bg-teal-700'} rounded-xl text-white shadow-sm px-5 py-4`} data-testid="digest-mgmt-banner">
              <p className="text-[11px] uppercase tracking-[0.2em] text-white/70 font-bold">{L.mgmtBanner}</p>
              <h2 className="text-lg font-bold mt-0.5 flex items-center gap-2 flex-wrap">
                {L.mgmtTitle}（{detail.week_start} 〜 {detail.week_end}）
                {versioned && (viewVersion || detail.active_version_id) && (
                  <span className="inline-flex items-center gap-1 rounded-full bg-white/15 px-2 py-0.5 text-[11px] font-semibold" data-testid="digest-shown-version">
                    v{viewVersion ? viewVersion.version_number : detail.active_version_number}
                    <LifecycleBadge v={viewVersion || { id: detail.active_version_id, lifecycle: 'active' }} lang={lang} testId="digest-shown-lifecycle" />
                  </span>
                )}
              </h2>
              <p className="text-[12px] text-white/80 mt-1">{L.stats(shown)}</p>
            </div>

            <div className="bg-white rounded-xl border border-zinc-200 shadow-sm p-5 space-y-4" data-testid="digest-mgmt-card">
              {mgmt.executive_summary && (
                <MgmtSection title={L.execSummary} Icon={Presentation} tone="text-violet-700">
                  <p className="text-[13px] text-zinc-700 leading-relaxed">{mgmt.executive_summary}</p>
                </MgmtSection>
              )}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <MgmtSection title={L.accomplishments} Icon={Sparkle} tone="text-emerald-700"><List items={mgmt.accomplishments} /></MgmtSection>
                <MgmtSection title={L.activeProjects} Icon={Kanban} tone="text-sky-700"><List items={mgmt.active_projects} /></MgmtSection>
                <MgmtSection title={L.risks} Icon={Warning} tone="text-amber-700"><List items={mgmt.risks} /></MgmtSection>
                <MgmtSection title={L.blocked} Icon={Warning} tone="text-rose-700"><List items={mgmt.blocked} /></MgmtSection>
                <MgmtSection title={L.lessons} Icon={Lightbulb} tone="text-violet-700"><List items={mgmt.lessons} /></MgmtSection>
                <MgmtSection title={L.upcoming} Icon={CalendarBlank} tone="text-teal-700"><List items={mgmt.upcoming} /></MgmtSection>
              </div>
              {asArr(mgmt.themes).length > 0 && (
                <div className="pt-2 border-t border-zinc-200">
                  <p className="text-[11px] font-bold text-zinc-400 uppercase tracking-wider mb-1.5">{L.themes}</p>
                  <div className="flex flex-wrap gap-1.5">
                    {asArr(mgmt.themes).map((t, i) => (
                      <span key={i} className="text-[11px] font-semibold px-2 py-0.5 rounded-full bg-indigo-50 text-indigo-700 border border-indigo-200">{t}</span>
                    ))}
                  </div>
                </div>
              )}
              {/* raw_fallback intentionally hidden: get_digest self-heals it, and
                  dumping raw JSON into the exec summary card looked broken. If
                  self-heal fails we surface a small hint + regenerate CTA instead. */}
              {!versioned && (detail.raw_fallback || detail.raw_engineer_fallback || detail.raw_project_fallback) &&
               !mgmt.executive_summary && (
                <div className="border-t border-zinc-200 pt-3 flex items-center justify-between gap-2 text-[12px]">
                  <span className="text-amber-700">⚠ AIの応答を解析できませんでした。再生成してください。</span>
                  <Button size="sm" variant="outline" onClick={() => regenerate()} className="h-7 text-[11px]" data-testid="digest-regenerate-inline-btn">
                    再生成
                  </Button>
                </div>
              )}
            </div>

            <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
              <div className="bg-white rounded-xl border border-zinc-200 shadow-sm p-5" data-testid="digest-engineers-card">
                <h3 className="text-[13px] font-bold text-zinc-800 flex items-center gap-1.5 mb-3">
                  <UserCircle size={15} weight="duotone" className="text-teal-600" /> {L.engineers}
                </h3>
                <div className="space-y-3">
                  {engSums.map((e, i) => (
                    <div key={i} className="rounded-lg border border-zinc-100 bg-zinc-50/50 px-3 py-2.5">
                      <p className="text-[13px] font-bold text-zinc-800">{e.engineer}</p>
                      {asArr(e.projects).length > 0 && <p className="text-[11px] text-zinc-500 mt-0.5">{L.projLabel}: {asArr(e.projects).join('、')}</p>}
                      <div className="mt-1.5 space-y-1 text-[12px]">
                        {asArr(e.accomplishments).length > 0 && <div><span className="font-semibold text-emerald-700">{L.done}:</span> {asArr(e.accomplishments).join(' / ')}</div>}
                        {asArr(e.blockers).length > 0 && <div><span className="font-semibold text-rose-600">{L.blockers}:</span> {asArr(e.blockers).join(' / ')}</div>}
                        {asArr(e.next_actions).length > 0 && <div><span className="font-semibold text-sky-700">{L.nextActions}:</span> {asArr(e.next_actions).join(' / ')}</div>}
                      </div>
                    </div>
                  ))}
                  {engSums.length === 0 && <p className="text-[12px] text-zinc-400">—</p>}
                </div>
              </div>

              <div className="bg-white rounded-xl border border-zinc-200 shadow-sm p-5" data-testid="digest-projects-card">
                <h3 className="text-[13px] font-bold text-zinc-800 flex items-center gap-1.5 mb-3">
                  <Kanban size={15} weight="duotone" className="text-indigo-600" /> {L.projects}
                </h3>
                <div className="space-y-3">
                  {projSums.map((p, i) => (
                    <div key={i} className="rounded-lg border border-zinc-100 bg-zinc-50/50 px-3 py-2.5">
                      <p className="text-[13px] font-bold text-zinc-800">{p.project}</p>
                      <div className="mt-1.5 space-y-1 text-[12px]">
                        {asArr(p.completed).length > 0 && <div><span className="font-semibold text-emerald-700">{L.completed}:</span> {asArr(p.completed).join(' / ')}</div>}
                        {asArr(p.ongoing).length > 0 && <div><span className="font-semibold text-sky-700">{L.ongoing}:</span> {asArr(p.ongoing).join(' / ')}</div>}
                        {asArr(p.blockers).length > 0 && <div><span className="font-semibold text-rose-600">{L.blockers}:</span> {asArr(p.blockers).join(' / ')}</div>}
                        {asArr(p.next_week).length > 0 && <div><span className="font-semibold text-violet-700">{L.nextWeek}:</span> {asArr(p.next_week).join(' / ')}</div>}
                      </div>
                    </div>
                  ))}
                  {projSums.length === 0 && <p className="text-[12px] text-zinc-400">—</p>}
                </div>
              </div>
            </div>

            {shown.ai_feedback && (
              <div className="bg-white rounded-xl border border-zinc-200 shadow-sm p-5" data-testid="digest-ai-feedback-card">
                <h3 className="text-[13px] font-bold text-zinc-800 flex items-center gap-1.5">
                  <Sparkle size={15} weight="duotone" className="text-rose-500" /> {L.aiFeedback}
                  <span className="ml-2 flex items-center gap-1.5 font-normal">
                    <span className="rounded-full border border-emerald-200 bg-emerald-50 px-2 py-0.5 text-[10.5px] font-semibold text-emerald-700">👍 {shown.ai_feedback.up}</span>
                    <span className="rounded-full border border-rose-200 bg-rose-50 px-2 py-0.5 text-[10.5px] font-semibold text-rose-700">👎 {shown.ai_feedback.down}</span>
                  </span>
                </h3>
                <p className="text-[11px] text-zinc-400 mt-0.5">{L.aiFbHint}</p>
                <div className="mt-3 space-y-2">
                  {asArr(shown.ai_feedback.low_rated).length === 0 ? (
                    <p className="text-[12px] text-zinc-400">{L.aiFbNone}</p>
                  ) : asArr(shown.ai_feedback.low_rated).map((r, i) => (
                    <div key={i} className="rounded-lg border border-rose-100 bg-rose-50/40 px-3 py-2.5" data-testid={`digest-ai-fb-${i}`}>
                      <p className="text-[12.5px] font-semibold text-zinc-800">{L.aiFbQ}: {r.question || '—'}</p>
                      <p className="mt-0.5 line-clamp-2 text-[11.5px] text-zinc-500">{L.aiFbA}: {r.answer_preview}</p>
                      <p className="mt-0.5 text-[10px] text-zinc-400">{jstDateTimeShort(r.ts)} · {r.user_name}{r.tool ? ` · ${r.tool}` : ''}</p>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        ) : (
          <div className="bg-white rounded-xl border border-dashed border-zinc-300 px-5 py-16 text-center" data-testid="digest-empty">
            <Sparkle size={32} weight="duotone" className="mx-auto text-zinc-300" />
            <p className="mt-2 text-sm text-zinc-500">「今週分を今すぐ生成」を押すと、提出済みの日報からAIがサマリーを作成します</p>
          </div>
        )}
      </div>

      <Dialog open={regenOpen} onOpenChange={setRegenOpen}>
        <DialogContent className="sm:max-w-md" data-testid="digest-regen-dialog">
          <DialogHeader><DialogTitle>ダイジェスト全体を再生成しますか？ / Regenerate as a new candidate?</DialogTitle></DialogHeader>
          <p className="text-[13px] text-zinc-600 whitespace-pre-line" data-testid="digest-regen-text">
            日報データから新しい候補バージョンを作成します。
            現在の有効版・英訳・過去のバージョンは変更されず、そのまま閲覧できます。
            生成後に内容を比較・確認し、明示的に「有効化」した時点で初めて有効版が切り替わります。
            {"\n"}A new candidate version is generated from the daily reports. The current active version,
            its translation and all history stay untouched until you explicitly activate the candidate.
            {"\n\n"}英訳だけを修正する場合は「英訳のみ再生成」を使用してください。
          </p>
          <DialogFooter>
            <Button variant="outline" onClick={() => setRegenOpen(false)} data-testid="digest-regen-cancel">キャンセル</Button>
            <Button onClick={regenerate} className="bg-teal-700 hover:bg-teal-800" data-testid="digest-regen-ok">
              新しい候補を生成
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={enRegenOpen} onOpenChange={setEnRegenOpen}>
        <DialogContent className="sm:max-w-md" data-testid="digest-en-regen-dialog">
          <DialogHeader><DialogTitle>英訳のみ再生成しますか？</DialogTitle></DialogHeader>
          <p className="text-[13px] text-zinc-600">
            保存済みの日本語ダイジェストを変更せず、英訳のみを再生成します。
          </p>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEnRegenOpen(false)} data-testid="digest-en-regen-cancel">キャンセル</Button>
            <Button onClick={() => { setEnRegenOpen(false); translate(sel, { force: true }); }}
              className="bg-teal-700 hover:bg-teal-800" data-testid="digest-en-regen-ok">
              <Translate size={13} className="mr-1" /> 英訳を再生成
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={historyOpen} onOpenChange={setHistoryOpen}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto" data-testid="digest-history-dialog">
          <DialogHeader><DialogTitle className="flex items-center gap-2">
            <ClockCounterClockwise size={16} weight="duotone" className="text-indigo-600" />
            英訳履歴
          </DialogTitle></DialogHeader>
          {historyRows === null ? (
            <div className="py-10 text-center text-sm text-zinc-400">読み込み中...</div>
          ) : historyRows.length === 0 ? (
            <div className="py-10 text-center text-sm text-zinc-400">履歴がありません</div>
          ) : (
            <div className="space-y-2">
              {historyRows.map((r) => (
                <div key={r.id} className="rounded-lg border border-zinc-200 px-3 py-2"
                  data-testid={`digest-history-row-${r.id}`}>
                  <div className="flex items-center justify-between gap-2">
                    <div className="min-w-0">
                      <div className="flex items-center gap-1.5 flex-wrap">
                        <span className="text-[11px] font-semibold text-zinc-500">
                          #v{r.translation_revision}
                        </span>
                        <TranslationStatusBadge status={r.translation_status}
                          active={r.is_active} />
                        <span className="text-[10px] text-zinc-400">
                          日本語 rev.{r.source_japanese_revision}
                        </span>
                      </div>
                      <p className="text-[11px] text-zinc-500 mt-0.5">
                        {r.action_type === 'english_only_regeneration' ? '英訳のみ再生成'
                          : r.action_type === 'full_digest_regeneration' ? '全体再生成'
                          : r.action_type === 'initial_translation' ? '初回翻訳' : r.action_type}
                        {' · '}{fmtDT(r.created_at)}{' · '}
                        {r.translation_generated_by || '—'}
                      </p>
                      {r.translation_error_message && (
                        <p className="text-[11px] text-rose-600 mt-0.5 whitespace-pre-wrap">
                          {r.translation_error_code}: {r.translation_error_message}
                        </p>
                      )}
                    </div>
                    {r.translation_status === 'complete' && !r.is_active && (
                      <Button size="sm" variant="outline"
                        onClick={() => restoreTranslation(r.id)}
                        className="h-7 text-[11px]"
                        data-testid={`digest-history-restore-${r.id}`}>
                        <ArrowUUpLeft size={12} className="mr-1" /> 復元
                      </Button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={deleteOpen} onOpenChange={setDeleteOpen}>
        <DialogContent className="sm:max-w-md" data-testid="digest-delete-dialog">
          <DialogHeader><DialogTitle>{VLT.archiveTitle}</DialogTitle></DialogHeader>
          <p className="text-[13px] text-zinc-600" data-testid="digest-archive-text">
            {detail?.week_start} 〜 {detail?.week_end}：{VLT.archiveBody}
          </p>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteOpen(false)} data-testid="digest-delete-cancel">{VLT.cancel}</Button>
            <Button onClick={remove} className="bg-rose-600 hover:bg-rose-700" data-testid="digest-delete-ok">{VLT.archive}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {detail && (
        <DigestVersionsPanel sid={detail.id} open={versionsOpen} onOpenChange={setVersionsOpen} lang={lang} can={can}
          refreshKey={versionsRefresh}
          onView={(v) => { setVersionsOpen(false); openVersion(detail.id, v.id); }}
          onActivate={(v) => setActivateTarget(v)}
          onRestore={(v) => setRestoreTarget(v)}
          onCompare={(a, b) => setComparePair([a, b])} />
      )}
      {detail && <DigestCompareDialog sid={detail.id} pair={comparePair} onClose={() => setComparePair(null)} lang={lang} />}
      {detail && (
        <DigestActivateDialog sid={detail.id} version={activateTarget} activeVersionId={detail.active_version_id}
          activeVersionNumber={detail.active_version_number} canWarn={can.activateWarn} lang={lang}
          onClose={() => setActivateTarget(null)}
          onDone={async () => { setActivateTarget(null); setVersionsOpen(false); await afterVersionChange(); }} />
      )}
      {detail && (
        <DigestRestoreDialog sid={detail.id} version={restoreTarget} lang={lang}
          onClose={() => setRestoreTarget(null)}
          onDone={async (res) => { setRestoreTarget(null); setVersionsOpen(false); await afterVersionChange(); if (res?.version?.id) openVersion(detail.id, res.version.id); }} />
      )}

      <Dialog open={settingsOpen} onOpenChange={setSettingsOpen}>
        <DialogContent className="sm:max-w-md" data-testid="digest-settings-dialog">
          <DialogHeader><DialogTitle>日報の自動化設定</DialogTitle></DialogHeader>
          {settings && (
            <div className="space-y-4">
              <div className="rounded-lg border border-zinc-200 p-3 space-y-2.5">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-[13px] font-semibold text-zinc-800">週間AIダイジェスト（自動生成）</p>
                    <p className="text-[11px] text-zinc-400">生成時に管理者へBot通知が届きます</p>
                  </div>
                  <Switch checked={settings.digest_enabled} onCheckedChange={(v) => setSettings((s) => ({ ...s, digest_enabled: v }))} data-testid="settings-digest-enabled" />
                </div>
                <div className="flex items-center gap-2">
                  <Select value={String(settings.digest_day)} onValueChange={(v) => setSettings((s) => ({ ...s, digest_day: Number(v) }))}>
                    <SelectTrigger className="h-9 w-28" data-testid="settings-digest-day"><SelectValue /></SelectTrigger>
                    <SelectContent>{DOW.map((d, i) => <SelectItem key={i} value={String(i)}>{d}曜日</SelectItem>)}</SelectContent>
                  </Select>
                  <Select value={String(settings.digest_hour)} onValueChange={(v) => setSettings((s) => ({ ...s, digest_hour: Number(v) }))}>
                    <SelectTrigger className="h-9 w-24" data-testid="settings-digest-hour"><SelectValue /></SelectTrigger>
                    <SelectContent>{Array.from({ length: 24 }, (_, h) => <SelectItem key={h} value={String(h)}>{h}:00</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div className="pt-2 border-t border-zinc-200">
                  <p className="text-[12px] font-semibold text-zinc-700">週の開始曜日</p>
                  <p className="text-[11px] text-zinc-400 mb-1.5">ダイジェスト対象期間の起点（開始日から7日間）。今週分の抽出やチームストリークにも適用されます。</p>
                  <Select
                    value={String(settings.week_start_day ?? 0)}
                    onValueChange={(v) => setSettings((s) => ({ ...s, week_start_day: Number(v) }))}
                  >
                    <SelectTrigger className="h-9 w-40" data-testid="settings-week-start-day"><SelectValue /></SelectTrigger>
                    <SelectContent>{DOW.map((d, i) => <SelectItem key={i} value={String(i)}>{d}曜日開始</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div className="pt-2 border-t border-zinc-200">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-[12px] font-semibold text-zinc-700">メール配信</p>
                      <p className="text-[11px] text-zinc-400">生成完了時に管理者/マネージャー宛にダイジェストをメール送信します。</p>
                    </div>
                    <Switch
                      checked={!!settings.email_digest_enabled}
                      onCheckedChange={(v) => setSettings((s) => ({ ...s, email_digest_enabled: v }))}
                      data-testid="settings-email-digest-enabled"
                    />
                  </div>
                  {settings.email_digest_enabled && (
                    <div className="mt-2">
                      <p className="text-[11px] text-zinc-500 mb-1">追加宛先（カンマ区切り、最大20件）</p>
                      <input
                        type="text"
                        value={(settings.email_digest_extra_recipients || []).join(', ')}
                        onChange={(e) => {
                          const arr = e.target.value.split(',')
                            .map((s) => s.trim()).filter(Boolean).slice(0, 20);
                          setSettings((s) => ({ ...s, email_digest_extra_recipients: arr }));
                        }}
                        placeholder="director@sensbey.co.jp, board@sensbey.co.jp"
                        className="w-full rounded-lg border border-zinc-200 bg-white px-3 py-1.5 text-xs outline-none focus:border-teal-400"
                        data-testid="settings-email-extra-recipients"
                      />
                    </div>
                  )}
                </div>
              </div>
              <div className="rounded-lg border border-zinc-200 p-3 space-y-2.5">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-[13px] font-semibold text-zinc-800">未提出リマインダー（平日）</p>
                    <p className="text-[11px] text-zinc-400">未提出の技術メンバーへBot DMを送信します</p>
                  </div>
                  <Switch checked={settings.reminder_enabled} onCheckedChange={(v) => setSettings((s) => ({ ...s, reminder_enabled: v }))} data-testid="settings-reminder-enabled" />
                </div>
                <div className="flex items-center gap-2">
                  <Select value={String(settings.reminder_hour)} onValueChange={(v) => setSettings((s) => ({ ...s, reminder_hour: Number(v) }))}>
                    <SelectTrigger className="h-9 w-24" data-testid="settings-reminder-hour"><SelectValue /></SelectTrigger>
                    <SelectContent>{Array.from({ length: 24 }, (_, h) => <SelectItem key={h} value={String(h)}>{h}時</SelectItem>)}</SelectContent>
                  </Select>
                  <Select value={String(settings.reminder_minute)} onValueChange={(v) => setSettings((s) => ({ ...s, reminder_minute: Number(v) }))}>
                    <SelectTrigger className="h-9 w-24" data-testid="settings-reminder-minute"><SelectValue /></SelectTrigger>
                    <SelectContent>{[0, 10, 20, 30, 40, 50].map((m) => <SelectItem key={m} value={String(m)}>{m}分</SelectItem>)}</SelectContent>
                  </Select>
                </div>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setSettingsOpen(false)}>キャンセル</Button>
            <Button onClick={saveSettings} className="bg-teal-700 hover:bg-teal-800" data-testid="settings-save-btn">保存</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

const STATUS_STYLE = {
  complete: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  failed: 'bg-rose-50 text-rose-700 border-rose-200',
  outdated: 'bg-amber-50 text-amber-700 border-amber-200',
  cancelled: 'bg-zinc-50 text-zinc-600 border-zinc-200',
  running: 'bg-indigo-50 text-indigo-700 border-indigo-200',
};
const STATUS_LABEL = {
  complete: '完了', failed: '失敗', outdated: '古いバージョン',
  cancelled: '中止', running: '実行中',
};
function TranslationStatusBadge({ status, active }) {
  const s = status || 'unknown';
  const cls = STATUS_STYLE[s] || 'bg-zinc-50 text-zinc-600 border-zinc-200';
  return (
    <span className={`px-1.5 py-0.5 rounded border text-[10px] font-semibold ${cls}`}>
      {STATUS_LABEL[s] || s}{active && s === 'complete' ? ' · 現在使用中' : ''}
    </span>
  );
}

